<template>
    <v-data-table
        :headers="displayHeaders"
        :items="rows"
        :custom-sort="customSort"
        :loading="loading"
        loading-text="Φόρτωση βαθμολογίας..."
        no-data-text="Δεν υπάρχουν δεδομένα βαθμολογίας"
        class="standings-table no-global-table-hover elevation-1 rounded-lg"
        hide-default-footer
        :mobile-breakpoint="0"
        :items-per-page="-1"
        :item-class="rowClass"
    >
        <template v-slot:item.position="{ item }">
            <div class="position-cell">
                <span class="position-badge" :class="{ 'position-badge--leader': item.position === 1 }">
                    {{ item.position }}
                </span>
                <span class="trend" :class="getTrendClass(item)">
                    <v-icon x-small :class="getTrendClass(item)">{{ getTrendIcon(item) }}</v-icon>
                    <span v-if="getTrendDelta(item)">{{ Math.abs(getTrendDelta(item)) }}</span>
                </span>
            </div>
        </template>
        <template v-slot:item.club="{ item }">
            <div
                class="team-link"
                :class="{ 'team-link--disabled': !getTeamId(item) }"
                role="button"
                tabindex="0"
                @click="goToTeam(item)"
                @keydown.enter="goToTeam(item)"
            >
                <v-img
                    v-if="item.imageUrl"
                    :src="`${assetsUrl}/${item.imageUrl}`"
                    class="team-logo"
                    contain
                />
                <v-icon v-else class="team-logo team-logo--placeholder orange--text">
                    mdi-basketball
                </v-icon>
                <span class="team-name">{{ item.name }}</span>
            </div>
        </template>
        <template v-slot:item.standing="{ item }">
            <span class="points">{{ item.standing }}</span>
        </template>
        <template v-slot:item.gp="{ item }">
            {{ item.won + item.lost }}
        </template>
        <template v-slot:item.win_percentage="{ item }">
            {{ `${(item.won + item.lost) > 0 ? (item.won / (item.won + item.lost) * 100).toFixed(1) : 0}%` }}
        </template>
        <template v-slot:item.pointsPlus="{ item }">
            <span class="muted">{{ item.pointsPlus }}</span>
        </template>
        <template v-slot:item.pointsMinus="{ item }">
            <span class="muted">{{ item.pointsMinus }}</span>
        </template>
        <template v-slot:item.pointsDiff="{ item }">
            <span class="diff" :class="getDiffClass(item.pointsDiff)">{{ formatDiff(item.pointsDiff) }}</span>
        </template>
        <template v-slot:item.form="{ item }">
            <div class="form-cell">
                <span v-if="getLastFiveForm(item).length === 0" class="form-empty">-</span>
                <span
                    v-for="(result, index) in getLastFiveForm(item)"
                    :key="`form-${index}`"
                    class="form-letter"
                    :class="[
                        result === 'W' ? 'form-win' : 'form-loss',
                        { 'form-latest': index === getLastFiveForm(item).length - 1 }
                    ]"
                >
                    {{ result }}
                </span>
            </div>
        </template>
    </v-data-table>
</template>

<script>
    import { prepareRanking, getLastFiveForm, getTeamId } from '@/utils/standings'

    export default {
        props: {
            ranking: {
                type: Array,
                default: () => []
            },
            loading: {
                type: Boolean,
                default: false
            },
            // Row to highlight (e.g. the team whose page shows the table)
            highlightTeamId: {
                type: [Number, String],
                default: null
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                headers: [
                    { text: '#', value: 'position', width: '56px', align: 'center' },
                    { text: 'Club', value: 'club', align: 'start' },
                    { text: 'Points', value: 'standing', width: '50px', align: 'center' },
                    { text: 'GP', value: 'gp', width: '50px', align: 'center' },
                    { text: 'Won', value: 'won', width: '50px', align: 'center' },
                    { text: 'Lost', value: 'lost', width: '50px', align: 'center' },
                    { text: 'Win(%)', value: 'win_percentage', width: '50px', align: 'center' },
                    { text: 'Pts+', value: 'pointsPlus', width: '50px', align: 'center' },
                    { text: 'Pts-', value: 'pointsMinus', width: '50px', align: 'center' },
                    { text: '+/-', value: 'pointsDiff', width: '50px', align: 'center' },
                    { text: 'Form', value: 'form', width: '110px', align: 'center', sortable: false }
                ]
            }
        },
        computed: {
            rows() {
                return prepareRanking(this.ranking)
            },
            displayHeaders() {
                return this.headers.map(header => {
                    if (header.value === 'club') {
                        return {
                            ...header,
                            width: this.$vuetify.breakpoint.mobile ? '150px' : '260px'
                        }
                    }
                    return header
                })
            }
        },
        methods: {
            getLastFiveForm,
            getTeamId,
            rowClass(item) {
                return this.highlightTeamId && String(getTeamId(item)) === String(this.highlightTeamId) ? 'row--highlight' : ''
            },
            customSort(items, sortBy, sortDesc) {
                if (!Array.isArray(items)) return []

                const activeSortBy = Array.isArray(sortBy) ? sortBy[0] : sortBy
                const activeSortDesc = Array.isArray(sortDesc) ? sortDesc[0] : sortDesc

                if (!activeSortBy) {
                    return items
                }

                const sortedItems = [...items].sort((a, b) => {
                    const valueA = this.getSortValue(a, activeSortBy)
                    const valueB = this.getSortValue(b, activeSortBy)

                    if (typeof valueA === 'number' && typeof valueB === 'number') {
                        return valueA - valueB
                    }

                    const normalizedA = valueA === null || valueA === undefined ? '' : String(valueA).toLowerCase()
                    const normalizedB = valueB === null || valueB === undefined ? '' : String(valueB).toLowerCase()

                    if (normalizedA < normalizedB) return -1
                    if (normalizedA > normalizedB) return 1
                    return 0
                })

                return activeSortDesc ? sortedItems.reverse() : sortedItems
            },
            getSortValue(item, sortBy) {
                if (sortBy === 'club') {
                    return item?.name || ''
                }

                if (sortBy === 'form') {
                    const form = getLastFiveForm(item)
                    return form.reduce((acc, value) => acc + (value === 'W' ? 1 : 0), 0)
                }

                if (sortBy === 'gp') {
                    return (Number(item?.won) || 0) + (Number(item?.lost) || 0)
                }

                if (sortBy === 'win_percentage') {
                    const won = Number(item?.won) || 0
                    const lost = Number(item?.lost) || 0
                    const gamesPlayed = won + lost
                    return gamesPlayed > 0 ? (won / gamesPlayed) * 100 : 0
                }

                const directValue = item?.[sortBy]
                if (Number.isFinite(Number(directValue))) {
                    return Number(directValue)
                }

                return directValue || ''
            },
            getTrendIcon(item) {
                const currentPosition = Number(item?.position)
                const previousPosition = Number(item?.previousPosition)

                if (!Number.isFinite(currentPosition) || !Number.isFinite(previousPosition)) {
                    return 'mdi-equal'
                }

                if (currentPosition < previousPosition) {
                    return 'mdi-chevron-up'
                }

                if (currentPosition === previousPosition) {
                    return 'mdi-equal'
                }

                return 'mdi-chevron-down'
            },
            getTrendDelta(item) {
                const currentPosition = Number(item?.position)
                const previousPosition = Number(item?.previousPosition)

                if (!Number.isFinite(currentPosition) || !Number.isFinite(previousPosition)) {
                    return 0
                }

                return previousPosition - currentPosition
            },
            formatDiff(value) {
                const diff = Number(value) || 0
                return diff > 0 ? `+${diff}` : `${diff}`
            },
            getDiffClass(value) {
                const diff = Number(value) || 0
                if (diff > 0) return 'diff-positive'
                if (diff < 0) return 'diff-negative'
                return ''
            },
            getTrendClass(item) {
                const currentPosition = Number(item?.position)
                const previousPosition = Number(item?.previousPosition)

                if (!Number.isFinite(currentPosition) || !Number.isFinite(previousPosition) || currentPosition === previousPosition) {
                    return 'trend-neutral'
                }

                if (currentPosition < previousPosition) {
                    return 'trend-up'
                }

                return 'trend-down'
            },
            goToTeam(item) {
                const teamId = getTeamId(item)
                if (!teamId) {
                    return
                }
                this.$router.push({ name: 'Team', params: { id: teamId } })
            }
        }
    }
</script>

<style scoped>
/* ---------- Table shell ---------- */
.standings-table {
    overflow: hidden;
}

.standings-table ::v-deep .v-data-table__wrapper table > thead > tr > th {
    background: #0d0d0f !important;
    color: #fff !important;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
    border-bottom: 3px solid #ff7133 !important;
}

.standings-table ::v-deep .v-data-table__wrapper table > thead > tr > th .v-icon {
    color: #fff !important;
}

.standings-table ::v-deep .v-data-table__wrapper table > thead > tr > th:nth-child(2) {
    position: sticky !important;
    left: 56px;
    z-index: 3;
}

.standings-table ::v-deep .v-data-table__wrapper table > tbody > tr > td {
    height: 56px;
    padding: 0 10px;
    white-space: nowrap;
    background: #fff;
}

.standings-table ::v-deep .v-data-table__wrapper table > tbody > tr > td:nth-child(2) {
    position: sticky;
    left: 56px;
    z-index: 2;
    box-shadow: 6px 0 8px -6px rgba(0, 0, 0, 0.15);
}

.standings-table ::v-deep .v-data-table__wrapper table > tbody > tr:nth-child(even) > td {
    background: #f7f7f8 !important;
}

.standings-table ::v-deep .v-data-table__wrapper table > tbody > tr:hover > td {
    background: #fff3ec !important;
}

/* The team whose page we are on */
.standings-table ::v-deep .v-data-table__wrapper table > tbody > tr.row--highlight > td {
    background: #fff3ec !important;
    font-weight: 700;
}

.standings-table ::v-deep .v-data-table__wrapper table > tbody > tr.row--highlight > td:first-child {
    box-shadow: inset 4px 0 0 #ff7133;
}

.standings-table ::v-deep .v-data-table__wrapper table > tbody > tr:hover > td:first-child {
    box-shadow: inset 4px 0 0 #ff7133;
}

/* ---------- Position ---------- */
.position-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
}

.position-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 24px;
    padding: 0 6px;
    border-radius: 6px;
    background: #0d0d0f;
    color: #fff;
    font-weight: 700;
    font-size: 0.85rem;
}

.position-badge--leader {
    background: linear-gradient(135deg, #f5c542, #d99a00);
    color: #1a1a1a;
}

.trend {
    display: inline-flex;
    align-items: center;
    font-size: 0.65rem;
    font-weight: 700;
    line-height: 1;
}

.trend-up {
    color: #2e7d32 !important;
}

.trend-neutral {
    color: #9e9e9e !important;
}

.trend-down {
    color: #c62828 !important;
}

/* ---------- Team ---------- */
.team-link {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
}

.team-link--disabled {
    cursor: default;
}

.team-logo {
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    font-size: 32px !important;
}

.team-name {
    font-weight: 700;
    white-space: normal;
    word-break: break-word;
    line-height: 1.2;
    text-align: left;
    transition: color 0.15s ease;
}

.team-link:hover .team-name {
    color: #ef4444;
}

/* ---------- Numbers ---------- */
.points {
    font-size: 1.05rem;
    font-weight: 800;
}

.muted {
    color: rgba(0, 0, 0, 0.5);
}

.diff {
    font-weight: 700;
}

.diff-positive {
    color: #2e7d32;
}

.diff-negative {
    color: #c62828;
}

/* ---------- Form (latest result is the last one) ---------- */
.form-cell {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.form-letter {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    font-weight: 700;
    font-size: 0.7rem;
    color: #fff;
    opacity: 0.75;
}

.form-latest {
    width: 26px;
    height: 26px;
    font-size: 0.8rem;
    opacity: 1;
}

.form-win {
    background-color: #2e7d32;
}

.form-win.form-latest {
    box-shadow: 0 0 0 2px #fff, 0 0 0 3px #2e7d32;
}

.form-loss {
    background-color: #c62828;
}

.form-loss.form-latest {
    box-shadow: 0 0 0 2px #fff, 0 0 0 3px #c62828;
}

.form-empty {
    color: rgba(0, 0, 0, 0.54);
    font-weight: 600;
}

/* ---------- Mobile ---------- */
@media (max-width: 600px) {
    .standings-table ::v-deep .v-data-table__wrapper table > tbody > tr > td {
        height: 52px;
        padding: 0 6px;
    }

    .team-logo {
        flex-basis: 28px;
        width: 28px;
        height: 28px;
        font-size: 26px !important;
    }

    .team-link {
        gap: 6px;
    }

    .team-name {
        font-size: 0.85rem;
    }
}
</style>
